import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, AlertCircle, Mail, MessageCircle, Phone, Check, X, FileText,
  RefreshCw, Search, Users, Filter, Clock, Euro, ExternalLink, Send
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const LOST_REASONS = [
  { value: 'prezzo_troppo_alto', label: 'Prezzo troppo alto' },
  { value: 'cliente_silenzioso', label: 'Cliente silenzioso' },
  { value: 'scelto_competitor', label: 'Scelto competitor' },
  { value: 'progetto_sospeso', label: 'Progetto sospeso/annullato' },
  { value: 'altro', label: 'Altro' },
];

const AGE_BUCKETS = [
  { key: 'all', label: 'Tutti' },
  { key: '3-7', label: '3-7 gg', min: 3, max: 7 },
  { key: '7-14', label: '7-14 gg', min: 7, max: 14 },
  { key: '14-30', label: '14-30 gg', min: 14, max: 30 },
  { key: '30+', label: '30+ gg', min: 30 },
];

const SEGMENT_BUCKETS = [
  { key: 'all', label: 'Tutti', activeColor: 'bg-amber-100 border-amber-400 text-amber-800' },
  { key: 'fresh', label: '🟢 Fresh (0-15gg)', activeColor: 'bg-green-100 border-green-400 text-green-800' },
  { key: 'mature', label: '🟡 Maturo (15-90gg)', activeColor: 'bg-yellow-100 border-yellow-400 text-yellow-800' },
  { key: 'at_risk', label: '🟠 A rischio (90-180gg)', activeColor: 'bg-orange-100 border-orange-400 text-orange-800' },
  { key: 'dormant', label: '🔴 Dormiente (180+gg)', activeColor: 'bg-red-100 border-red-400 text-red-800' },
];

const SEGMENT_STYLES = {
  fresh: 'bg-green-100 text-green-800',
  mature: 'bg-yellow-100 text-yellow-800',
  at_risk: 'bg-orange-100 text-orange-800',
  dormant: 'bg-red-100 text-red-800',
};

const SEGMENT_LABELS = {
  fresh: '🟢 Fresh',
  mature: '🟡 Maturo',
  at_risk: '🟠 A rischio',
  dormant: '🔴 Dormiente',
};

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

// Pulisce numero per wa.me (rimuove tutto tranne le cifre; assume prefisso già incluso)
const cleanPhone = (p) => (p || '').replace(/[^0-9]/g, '');

const buildWhatsAppLink = (phone, message) => {
  const num = cleanPhone(phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(message || '')}`;
};

const buildMailto = (email, subject, body) => {
  if (!email) return null;
  return `mailto:${email}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`;
};

// Template messaggi per fasce di età preventivi
const getQuoteTemplate = (item, days) => {
  const name = item.customer_name || 'Ciao';
  const num = item.quote_number || '';
  const valore = fmtEUR(item.total);

  if (days < 7) {
    return {
      email_subject: `Preventivo ${num} — posso aiutarti?`,
      email_body: `Ciao ${name},\n\nti ho inviato il preventivo ${num} (${valore}) qualche giorno fa. Hai avuto modo di darci un'occhiata? Sono qui se hai domande o ti serve qualche personalizzazione.\n\nA presto,\nDavide — IlTuoAmaro.it`,
      whatsapp: `Ciao ${name}! Ti scrivo da IlTuoAmaro.it. Hai avuto modo di vedere il preventivo ${num} che ti ho inviato? Posso chiarirti dubbi o personalizzarlo per te 😊`,
    };
  }
  if (days < 14) {
    return {
      email_subject: `Preventivo ${num} — ti racconto un caso simile`,
      email_body: `Ciao ${name},\n\nvolevo capire se il preventivo ${num} ti era utile. Recentemente un cliente con esigenze simili ha realizzato il suo amaro personalizzato in 15 giorni — se ti va ti racconto come.\n\nFammi sapere,\nDavide — IlTuoAmaro.it`,
      whatsapp: `Ciao ${name}! Sul preventivo ${num} ti era utile? Se ti va ti racconto un caso simile che ho chiuso di recente — magari ti aiuta a decidere 🍷`,
    };
  }
  if (days < 30) {
    return {
      email_subject: `Sconto dedicato sul preventivo ${num}`,
      email_body: `Ciao ${name},\n\nse vuoi confermare il preventivo ${num} entro 7 giorni, posso applicarti uno sconto del 5%. Fammi sapere se ti interessa, sono qui.\n\nDavide — IlTuoAmaro.it`,
      whatsapp: `Ciao ${name}! Se vuoi confermare il preventivo ${num} entro 7 giorni ti applico uno sconto del 5%. Mi fai sapere?`,
    };
  }
  return {
    email_subject: `Chiudiamo? Dimmi cosa ti ha frenato`,
    email_body: `Ciao ${name},\n\nnon voglio insistere — vorrei solo capire se il preventivo ${num} è ancora di interesse o se devo archiviarlo. Mi basta un sì/no e, se vuoi, due righe sul motivo. Mi aiuti a migliorare.\n\nGrazie,\nDavide — IlTuoAmaro.it`,
    whatsapp: `Ciao ${name}, ti chiedo solo un sì/no: il preventivo ${num} è ancora di interesse? Se no, archivio. Grazie!`,
  };
};

const getUserTemplate = (item, days) => {
  const name = item.first_name || item.company_name || 'Ciao';
  if (days < 30) {
    return {
      email_subject: `${name}, configura il tuo amaro in 3 minuti`,
      email_body: `Ciao ${name},\n\nti sei iscritto a IlTuoAmaro.it ma non hai ancora provato il configuratore. In 3 minuti puoi avere un preventivo personalizzato — vuoi che ti accompagni?\n\nA presto,\nDavide — IlTuoAmaro.it`,
      whatsapp: `Ciao ${name}! Ti sei iscritto a IlTuoAmaro.it ma non hai ancora provato il configuratore. Posso aiutarti? 🍷`,
    };
  }
  return {
    email_subject: `${name}, sconto 10% sul primo ordine`,
    email_body: `Ciao ${name},\n\nti sei iscritto qualche settimana fa. Per ringraziarti ti offro uno sconto del 10% sul primo ordine: usa il codice WELCOME10 entro 14 giorni.\n\nDavide — IlTuoAmaro.it`,
    whatsapp: `Ciao ${name}! Per ringraziarti dell'iscrizione ti offro sconto del 10% sul primo ordine: codice WELCOME10, valido 14 giorni 🎁`,
  };
};

const channelBadge = (channel) => {
  const map = {
    email: { label: 'Email', color: 'bg-blue-100 text-blue-700' },
    whatsapp: { label: 'WhatsApp', color: 'bg-green-100 text-green-700' },
    phone: { label: 'Telefono', color: 'bg-purple-100 text-purple-700' },
    other: { label: 'Altro', color: 'bg-gray-100 text-gray-700' },
  };
  const c = map[channel] || map.other;
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.color}`}>{c.label}</span>;
};

const AdminCrmFunnel = () => {
  usePageTitle('CRM Funnel - Admin');
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState('quotes'); // quotes | users | post_sales
  const [stats, setStats] = useState(null);
  const [lostReasons, setLostReasons] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [postSales, setPostSales] = useState([]);
  const [postSalesTotals, setPostSalesTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ageBucket, setAgeBucket] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');

  // Modal state
  const [actionModal, setActionModal] = useState(null);
  // { entity_type, entity_id, action, default_channel, label }
  const [emailModal, setEmailModal] = useState(null);
  // { entity_type, entity_id, to_email, label }
  const [whatsappModal, setWhatsappModal] = useState(null);
  // { entity_type, entity_id, phone, label }

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const loadAll = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${adminToken}` };
      const [statsRes, quotesRes, usersRes, postSalesRes, lostReasonsRes] = await Promise.all([
        fetch(`${apiUrl}/api/crm/funnel/stats`, { headers }),
        fetch(`${apiUrl}/api/crm/funnel/dormant-quotes`, { headers }),
        fetch(`${apiUrl}/api/crm/funnel/dormant-users`, { headers }),
        fetch(`${apiUrl}/api/crm/funnel/post-sales`, { headers }),
        fetch(`${apiUrl}/api/crm/funnel/stats/lost-reasons?days=90`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (lostReasonsRes.ok) setLostReasons(await lostReasonsRes.json());
      if (quotesRes.ok) {
        const d = await quotesRes.json();
        setQuotes(d.items || []);
      }
      if (usersRes.ok) {
        const d = await usersRes.json();
        setUsers(d.items || []);
      }
      if (postSalesRes.ok) {
        const d = await postSalesRes.json();
        setPostSales(d.items || []);
        setPostSalesTotals(d.totals || null);
      }
    } catch (e) {
      console.error('Errore caricamento funnel:', e);
      toast({ title: 'Errore', description: 'Impossibile caricare i dati del funnel', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [adminToken, apiUrl, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const logAction = async (payload) => {
    try {
      const res = await fetch(`${apiUrl}/api/crm/funnel/actions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore registrazione azione');
      }
      toast({ title: 'Azione registrata', description: payload.action === 'lost' ? 'Marcato come perso' : 'Marcato come contattato' });
      loadAll();
      setActionModal(null);
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    }
  };

  // Filtri lato client
  const filterByAge = (days) => {
    if (ageBucket === 'all') return true;
    const b = AGE_BUCKETS.find((x) => x.key === ageBucket);
    if (!b) return true;
    if (b.max != null) return days >= b.min && days < b.max;
    return days >= b.min;
  };
  const filterBySearch = (text) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (text || '').toLowerCase().includes(s);
  };

  const filteredQuotes = quotes.filter((q) =>
    filterByAge(q.days_since_created) &&
    filterBySearch([q.customer_name, q.customer_email, q.quote_number].join(' '))
  );
  const filteredUsers = users.filter((u) =>
    filterByAge(u.days_since_signup) &&
    filterBySearch([u.first_name, u.last_name, u.company_name, u.email].join(' '))
  );
  const filteredPostSales = postSales.filter((c) => {
    if (segmentFilter !== 'all' && c.segment !== segmentFilter) return false;
    return filterBySearch([c.first_name, c.last_name, c.company_name, c.email].join(' '));
  });

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="crm-funnel-page">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-amber-600" />
              CRM Funnel — Lead Nurturing
            </h1>
            <p className="text-gray-600 mt-1 text-sm">Riattiva preventivi dormienti e utenti iscritti che non hanno ancora convertito</p>
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            data-testid="funnel-refresh-btn"
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>

        {/* Dashboard Metriche */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="funnel-stats">
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-50 text-xs uppercase tracking-wide">Preventivi dormienti</p>
                  <p className="text-3xl font-bold mt-1">{stats.dormant_quotes_count}</p>
                  <p className="text-amber-100 text-xs mt-1">≥ {stats.thresholds.quote_days} giorni di silenzio</p>
                </div>
                <FileText className="w-10 h-10 text-amber-200" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Valore preventivi</p>
                  <p className="text-2xl font-bold mt-1 text-gray-900">{fmtEUR(stats.dormant_quotes_value)}</p>
                  <p className="text-gray-400 text-xs mt-1">IVA inclusa</p>
                </div>
                <Euro className="w-10 h-10 text-gray-300" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">MOL potenziale</p>
                  <p className="text-2xl font-bold mt-1 text-green-700">{fmtEUR(stats.dormant_quotes_potential_mol)}</p>
                  <p className="text-gray-400 text-xs mt-1">Se chiudi tutti</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-200" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Utenti dormienti</p>
                  <p className="text-3xl font-bold mt-1 text-gray-900">{stats.dormant_users_count}</p>
                  <p className="text-gray-400 text-xs mt-1">≥ {stats.thresholds.user_days}gg senza preventivi</p>
                </div>
                <Users className="w-10 h-10 text-gray-300" />
              </div>
            </div>
          </div>
        )}

        {/* Mini stats azioni 30gg */}
        {stats && (
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm flex items-center gap-4">
            <span className="text-gray-500">📊 Ultimi 30 giorni:</span>
            <span className="text-blue-700 font-medium">{stats.actions_last_30d.contacted} contattati</span>
            <span className="text-gray-400">·</span>
            <span className="text-red-600 font-medium">{stats.actions_last_30d.lost} marcati persi</span>
          </div>
        )}

        {/* Motivi di perdita (ultimi 90gg) — Analytics Fase P1 */}
        {lostReasons && lostReasons.total > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="lost-reasons-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">📉 Motivi di perdita — ultimi 90 giorni</h3>
                <p className="text-xs text-gray-500 mt-0.5">Su {lostReasons.total} clienti/preventivi persi</p>
              </div>
            </div>
            <div className="space-y-2">
              {lostReasons.data
                .filter((r) => r.count > 0)
                .map((r) => {
                  const labels = {
                    prezzo_troppo_alto: '💰 Prezzo troppo alto',
                    cliente_silenzioso: '🔕 Cliente silenzioso',
                    scelto_competitor: '🏳️ Scelto competitor',
                    progetto_sospeso: '⏸️ Progetto sospeso',
                    altro: '➖ Altro',
                  };
                  const colors = {
                    prezzo_troppo_alto: 'bg-red-500',
                    cliente_silenzioso: 'bg-amber-500',
                    scelto_competitor: 'bg-fuchsia-500',
                    progetto_sospeso: 'bg-blue-500',
                    altro: 'bg-gray-400',
                  };
                  return (
                    <div key={r.reason} className="flex items-center gap-3" data-testid={`lost-reason-${r.reason}`}>
                      <span className="w-40 text-xs text-gray-700">{labels[r.reason] || r.reason}</span>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${colors[r.reason] || 'bg-gray-400'}`} style={{ width: `${r.percentage}%` }} />
                      </div>
                      <span className="w-16 text-right text-xs tabular-nums font-semibold text-gray-800">{r.percentage}%</span>
                      <span className="w-8 text-right text-[10px] text-gray-500 tabular-nums">({r.count})</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200 flex">
            <button
              onClick={() => setTab('quotes')}
              data-testid="tab-quotes"
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'quotes' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              📋 Preventivi dormienti ({quotes.length})
            </button>
            <button
              onClick={() => setTab('users')}
              data-testid="tab-users"
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'users' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              👥 Utenti dormienti ({users.length})
            </button>
            <button
              onClick={() => setTab('post_sales')}
              data-testid="tab-post-sales"
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'post_sales' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              💎 Post-Sales ({postSales.length})
            </button>
          </div>

          {/* Filtri */}
          <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, email, numero preventivo..."
                data-testid="funnel-search"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Filter className="w-4 h-4 text-gray-400" />
              {tab === 'post_sales' ? (
                <>
                  <span className="text-gray-500">Segmento:</span>
                  {SEGMENT_BUCKETS.map((b) => (
                    <button
                      key={b.key}
                      onClick={() => setSegmentFilter(b.key)}
                      data-testid={`segment-${b.key}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${segmentFilter === b.key ? `${b.activeColor}` : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    >
                      {b.label}
                    </button>
                  ))}
                </>
              ) : (
                <>
                  <span className="text-gray-500">Fascia età:</span>
                  {AGE_BUCKETS.map((b) => (
                    <button
                      key={b.key}
                      onClick={() => setAgeBucket(b.key)}
                      data-testid={`age-bucket-${b.key}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${ageBucket === b.key ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    >
                      {b.label}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Contenuto */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center text-gray-500">Caricamento…</div>
            ) : tab === 'quotes' ? (
              <QuotesTable
                items={filteredQuotes}
                onEmail={(it) => setEmailModal({ entity_type: 'quote', entity_id: it.id, to_email: it.customer_email, label: `Preventivo ${it.quote_number}` })}
                onContact={(it, channel) => setActionModal({ entity_type: 'quote', entity_id: it.id, action: 'contacted', default_channel: channel, label: `Preventivo ${it.quote_number}` })}
                onLost={(it) => setActionModal({ entity_type: 'quote', entity_id: it.id, action: 'lost', label: `Preventivo ${it.quote_number}` })}
              />
            ) : tab === 'users' ? (
              <UsersTable
                items={filteredUsers}
                onEmail={(it) => setEmailModal({ entity_type: 'user', entity_id: it.id, to_email: it.email, label: it.email })}
                onContact={(it, channel) => setActionModal({ entity_type: 'user', entity_id: it.id, action: 'contacted', default_channel: channel, label: it.email })}
                onLost={(it) => setActionModal({ entity_type: 'user', entity_id: it.id, action: 'lost', label: it.email })}
              />
            ) : (
              <PostSalesTable
                items={filteredPostSales}
                totals={postSalesTotals}
                onEmail={(it) => setEmailModal({ entity_type: 'customer', entity_id: it.user_id, to_email: it.email, label: it.company_name || `${it.first_name || ''} ${it.last_name || ''}`.trim() || it.email })}
                onContact={(it, channel) => setActionModal({ entity_type: 'customer', entity_id: it.user_id, action: 'contacted', default_channel: channel, label: it.email })}
                onLost={(it) => setActionModal({ entity_type: 'customer', entity_id: it.user_id, action: 'lost', label: it.email })}
              />
            )}
          </div>
        </div>

        {/* Action Modal */}
        {actionModal && (
          <ActionModal
            data={actionModal}
            onClose={() => setActionModal(null)}
            onSave={logAction}
          />
        )}

        {/* Email Send Modal */}
        {emailModal && (
          <EmailSendModal
            data={emailModal}
            onClose={() => setEmailModal(null)}
            onSent={() => { setEmailModal(null); loadAll(); }}
            adminToken={adminToken}
            apiUrl={apiUrl}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// ============ Sub-components ============

const QuotesTable = ({ items, onEmail, onContact, onLost }) => {
  if (items.length === 0) {
    return <div className="p-12 text-center text-gray-500">🎉 Nessun preventivo dormiente con questi filtri</div>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200" data-testid="funnel-quotes-table">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Preventivo</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assegnato a</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valore</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">MOL stim.</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Età</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ultima azione</th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {items.map((q) => {
          const days = q.days_since_created || 0;
          const tpl = getQuoteTemplate(q, days);
          const waLink = buildWhatsAppLink(q.customer_phone, tpl.whatsapp);
          const mailLink = buildMailto(q.customer_email, tpl.email_subject, tpl.email_body);
          const ageColor = days >= 30 ? 'text-red-600' : days >= 14 ? 'text-orange-600' : 'text-gray-700';

          return (
            <tr key={q.id} className="hover:bg-amber-50/40" data-testid={`funnel-quote-row-${q.id}`}>
              <td className="px-4 py-3">
                <Link to={`/admin/quotes/${q.id}`} className="text-blue-600 hover:underline text-sm font-medium">
                  {q.quote_number}
                </Link>
                <div className="text-xs text-gray-400 capitalize">{q.status}</div>
              </td>
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{q.customer_name || '—'}</div>
                <div className="text-xs text-gray-500">{q.customer_email}</div>
                {q.customer_phone && (
                  <div className="text-xs text-gray-400 flex items-center mt-0.5">
                    <Phone className="w-3 h-3 mr-1" />
                    {q.customer_phone}
                  </div>
                )}
              </td>
              <td className="px-4 py-3" data-testid={`funnel-quote-assignee-${q.id}`}>
                {q.assignee_name ? (
                  <div>
                    <div className="text-sm font-medium text-gray-700">{q.assignee_name}</div>
                    {q.assignee_email && <div className="text-xs text-gray-400">{q.assignee_email}</div>}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">Non assegnato</span>
                )}
              </td>
              <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtEUR(q.total)}</td>
              <td className="px-4 py-3">
                <span className={q.mol >= 0 ? 'text-green-700' : 'text-red-600'}>
                  <div className="text-sm font-semibold">{fmtEUR(q.mol)}</div>
                  <div className="text-xs">{q.mol_pct}%</div>
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={`text-sm font-semibold ${ageColor}`}>{days}gg</span>
              </td>
              <td className="px-4 py-3">
                {q.last_action ? (
                  <div className="text-xs">
                    {channelBadge(q.last_action.channel)}
                    <div className="text-gray-400 mt-0.5">
                      {new Date(q.last_action.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                  {q.customer_email && (
                    <button
                      type="button"
                      onClick={() => onEmail(q)}
                      data-testid={`funnel-quote-email-${q.id}`}
                      title="Invia email (preview + Brevo)"
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  {waLink && (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onContact(q, 'whatsapp')}
                      data-testid={`funnel-quote-whatsapp-${q.id}`}
                      title="Invia WhatsApp"
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => onContact(q, 'phone')}
                    data-testid={`funnel-quote-phone-${q.id}`}
                    title="Marca contattato (altro canale)"
                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onLost(q)}
                    data-testid={`funnel-quote-lost-${q.id}`}
                    title="Marca come perso"
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const UsersTable = ({ items, onEmail, onContact, onLost }) => {
  if (items.length === 0) {
    return <div className="p-12 text-center text-gray-500">🎉 Nessun utente dormiente con questi filtri</div>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200" data-testid="funnel-users-table">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Utente</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contatti</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Iscritto</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ultima azione</th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {items.map((u) => {
          const days = u.days_since_signup || 0;
          const tpl = getUserTemplate(u, days);
          const waLink = buildWhatsAppLink(u.phone, tpl.whatsapp);
          const mailLink = buildMailto(u.email, tpl.email_subject, tpl.email_body);
          const ageColor = days >= 60 ? 'text-red-600' : days >= 30 ? 'text-orange-600' : 'text-gray-700';

          return (
            <tr key={u.id} className="hover:bg-amber-50/40" data-testid={`funnel-user-row-${u.id}`}>
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">
                  {u.company_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                </div>
                {u.company_name && (u.first_name || u.last_name) && (
                  <div className="text-xs text-gray-500">{u.first_name} {u.last_name}</div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="text-xs text-gray-700">{u.email}</div>
                {u.phone && (
                  <div className="text-xs text-gray-400 flex items-center mt-0.5">
                    <Phone className="w-3 h-3 mr-1" />
                    {u.phone}
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 capitalize">
                  {u.user_type === 'business' ? '🏢 Business' : '👤 Privato'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={`text-sm font-semibold ${ageColor}`}>{days}gg fa</span>
              </td>
              <td className="px-4 py-3">
                {u.last_action ? (
                  <div className="text-xs">
                    {channelBadge(u.last_action.channel)}
                    <div className="text-gray-400 mt-0.5">
                      {new Date(u.last_action.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                  <Link
                    to={`/admin/customers/${u.id}`}
                    title="Vai al profilo CRM"
                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  {mailLink && (
                    <button
                      type="button"
                      onClick={() => onEmail(u)}
                      data-testid={`funnel-user-email-${u.id}`}
                      title="Invia email (preview + Brevo)"
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  {waLink && (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onContact(u, 'whatsapp')}
                      data-testid={`funnel-user-whatsapp-${u.id}`}
                      title="Invia WhatsApp"
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => onContact(u, 'phone')}
                    data-testid={`funnel-user-phone-${u.id}`}
                    title="Marca contattato"
                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onLost(u)}
                    data-testid={`funnel-user-lost-${u.id}`}
                    title="Marca come perso"
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const PostSalesTable = ({ items, totals, onEmail, onContact, onLost }) => {
  if (items.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        <p className="text-base font-medium">Nessun cliente acquisito da contattare</p>
        <p className="text-xs mt-2">I clienti compaiono qui dopo avere almeno 1 ordine in stato <code className="bg-gray-100 px-1 rounded">delivered</code> o <code className="bg-gray-100 px-1 rounded">completed</code>.</p>
      </div>
    );
  }
  return (
    <>
      {totals && (
        <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-white border-b border-amber-100 flex flex-wrap gap-4 text-sm" data-testid="post-sales-totals">
          <span><strong className="text-amber-700">{totals.total_customers}</strong> clienti</span>
          <span className="text-gray-400">·</span>
          <span>LTV totale: <strong>{fmtEUR(totals.total_ltv)}</strong></span>
          <span className="text-gray-400">·</span>
          <span className="text-green-700">🟢 {totals.by_segment.fresh}</span>
          <span className="text-yellow-700">🟡 {totals.by_segment.mature}</span>
          <span className="text-orange-700">🟠 {totals.by_segment.at_risk}</span>
          <span className="text-red-700">🔴 {totals.by_segment.dormant}</span>
        </div>
      )}
      <table className="min-w-full divide-y divide-gray-200" data-testid="funnel-post-sales-table">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Segmento</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">LTV</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ordini</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">AOV</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ultimo ordine</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Freq.</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prossimo previsto</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ultima azione</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {items.map((c) => {
            const seg = c.segment;
            const segStyle = SEGMENT_STYLES[seg] || 'bg-gray-100 text-gray-700';
            const segLabel = SEGMENT_LABELS[seg] || seg;
            // WhatsApp link (simple, no template - admin handles WA manually via wa.me)
            const waLink = c.phone ? `https://wa.me/${(c.phone || '').replace(/[^0-9]/g, '')}` : null;
            const predicted = c.predicted_next_order_at ? new Date(c.predicted_next_order_at) : null;
            const isOverdue = predicted && predicted < new Date();

            return (
              <tr key={c.user_id} className="hover:bg-amber-50/40" data-testid={`funnel-post-sales-row-${c.user_id}`}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">
                    {c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email}
                  </div>
                  <div className="text-xs text-gray-500">{c.email}</div>
                  {c.phone && (
                    <div className="text-xs text-gray-400 flex items-center mt-0.5">
                      <Phone className="w-3 h-3 mr-1" />{c.phone}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${segStyle}`}>
                    {segLabel}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">{fmtEUR(c.ltv)}</td>
                <td className="px-4 py-3 text-center text-sm text-gray-700">{c.orders_count}</td>
                <td className="px-4 py-3 text-right text-sm text-gray-700">{fmtEUR(c.aov)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {c.last_order_at ? new Date(c.last_order_at).toLocaleDateString('it-IT') : '—'}
                  <div className="text-gray-400">{c.days_since_last_order}gg fa</div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {c.avg_frequency_days != null ? `~${c.avg_frequency_days}gg` : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {predicted ? (
                    <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                      {predicted.toLocaleDateString('it-IT')}
                      {isOverdue && <div className="text-xs">in ritardo</div>}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {c.last_action ? (
                    <div className="text-xs">
                      {channelBadge(c.last_action.channel)}
                      <div className="text-gray-400 mt-0.5">
                        {new Date(c.last_action.created_at).toLocaleDateString('it-IT')}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    {c.email && (
                      <button
                        type="button"
                        onClick={() => onEmail(c)}
                        data-testid={`funnel-post-sales-email-${c.user_id}`}
                        title="Invia email (preview + Brevo)"
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                    )}
                    {waLink && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onContact(c, 'whatsapp')}
                        data-testid={`funnel-post-sales-whatsapp-${c.user_id}`}
                        title="Apri WhatsApp"
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => onContact(c, 'phone')}
                      data-testid={`funnel-post-sales-phone-${c.user_id}`}
                      title="Marca contattato"
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onLost(c)}
                      data-testid={`funnel-post-sales-lost-${c.user_id}`}
                      title="Marca come non-interessato"
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};


const ActionModal = ({ data, onClose, onSave }) => {
  const [channel, setChannel] = useState(data.default_channel || 'whatsapp');
  const [note, setNote] = useState('');
  const [lostReason, setLostReason] = useState('cliente_silenzioso');
  const isLost = data.action === 'lost';

  const handleSubmit = () => {
    const payload = {
      entity_type: data.entity_type,
      entity_id: data.entity_id,
      action: data.action,
      note: note.trim() || undefined,
    };
    if (isLost) {
      payload.lost_reason = lostReason;
    } else {
      payload.channel = channel;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="funnel-action-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              {isLost ? (
                <><AlertCircle className="w-5 h-5 mr-2 text-red-600" /> Marca come perso</>
              ) : (
                <><Check className="w-5 h-5 mr-2 text-green-600" /> Marca come contattato</>
              )}
            </h3>
            <p className="text-xs text-gray-500 mt-1">{data.label}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLost ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Motivo</span>
              <select
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                data-testid="funnel-lost-reason"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {LOST_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Canale usato</span>
              <div className="mt-1 grid grid-cols-4 gap-2">
                {[
                  { v: 'email', l: 'Email', icon: Mail },
                  { v: 'whatsapp', l: 'WhatsApp', icon: MessageCircle },
                  { v: 'phone', l: 'Telefono', icon: Phone },
                  { v: 'other', l: 'Altro', icon: Clock },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.v}
                      onClick={() => setChannel(opt.v)}
                      data-testid={`funnel-channel-${opt.v}`}
                      className={`flex flex-col items-center gap-1 p-2 border rounded-lg text-xs ${channel === opt.v ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    >
                      <Icon className="w-4 h-4" />
                      {opt.l}
                    </button>
                  );
                })}
              </div>
            </label>
          </div>
        )}

        <label className="block mt-3">
          <span className="text-sm font-medium text-gray-700">Nota (facoltativa)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows="3"
            data-testid="funnel-action-note"
            placeholder="Es: richiama lunedì alle 10, ha chiesto sconto..."
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            data-testid="funnel-action-submit"
            className={`px-4 py-2 rounded-lg text-sm text-white ${isLost ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  );
};


const EmailSendModal = ({ data, onClose, onSent, adminToken, apiUrl }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [ageBucket, setAgeBucket] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${apiUrl}/api/crm/funnel/templates/resolve?entity_type=${data.entity_type}&entity_id=${data.entity_id}&channel=email`,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Errore caricamento template');
        }
        const d = await res.json();
        setSubject(d.rendered?.subject || '');
        setBody(d.rendered?.body || '');
        setTemplateName(d.template?.name || '');
        setAgeBucket(d.age_bucket || '');
      } catch (e) {
        toast({ title: 'Errore', description: e.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast({ title: 'Compila tutti i campi', description: 'Oggetto e corpo sono obbligatori', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${apiUrl}/api/crm/funnel/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          entity_type: data.entity_type,
          entity_id: data.entity_id,
          to_email: data.to_email,
          subject,
          body,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore invio email');
      }
      toast({ title: 'Email inviata!', description: `Recapitata a ${data.to_email} via Brevo` });
      onSent();
    } catch (e) {
      toast({ title: 'Errore invio', description: e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const mailtoLink = `mailto:${data.to_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="funnel-email-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-blue-600" />
                Invia Email
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                A: <span className="font-medium">{data.to_email}</span> · {data.label}
              </p>
              {templateName && (
                <p className="text-xs text-amber-700 mt-1">
                  Template: {templateName} (fascia {ageBucket.replace('_', '-')})
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Carico template…
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="funnel-email-subject"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Corpo email</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  data-testid="funnel-email-body"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Verrà inviato come testo + HTML basico (newline → &lt;br&gt;)</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6 flex-wrap">
            <a
              href={mailtoLink}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 inline-flex items-center"
            >
              <Mail className="w-4 h-4 mr-2" />
              Apri in client email
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              onClick={handleSend}
              disabled={sending || loading}
              data-testid="funnel-email-send-brevo"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center disabled:opacity-50"
            >
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Invia via Brevo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCrmFunnel;
